import { isInternalUser } from '../utils/internal-user';

export interface PortalPublicSettings {
  clarityProjectId?: string;
  gaMeasurementId?: string;
  logoDevToken?: string;
  shopifyClientId?: string;
  microsoftIdpName?: string;
  maintenanceMode?: boolean;
}

export interface PortalRegion {
  id: string;
  label: string;
  origin: string;
  originAliases?: string[];
  awsRegion: string;
  enabled: boolean;
  internalOnly?: boolean;
  apiUrl?: string;
  publicApiUrl?: string;
  integrationsApiUrl?: string;
  webApiUrl?: string;
  transactionalApiUrl?: string;
  marketingApiUrl?: string;
  userPoolId?: string;
  clientId?: string;
  oauthDomain?: string;
  settings?: PortalPublicSettings;
}

const defaults: PortalRegion[] = [
  { id: 'us', label: 'United States (East)', origin: 'https://app.simplysend.email', originAliases: ['https://app.simplysend.dev'], awsRegion: 'us-east-1', enabled: true },
  { id: 'au', label: 'Australia (Sydney)', origin: 'https://au-app.simplysend.email', awsRegion: 'ap-southeast-2', enabled: false },
  { id: 'ca', label: 'Canada (Central)', origin: 'https://ca-app.simplysend.email', awsRegion: 'ca-central-1', enabled: false },
  { id: 'eu', label: 'Europe (Frankfurt)', origin: 'https://eu-app.simplysend.email', awsRegion: 'eu-central-1', enabled: false },
];

function isRegionalPortalHostname(regionId: string, hostname: string): boolean {
  return ['simplysend.email', 'simplysend.dev'].some(domain => hostname === `${regionId}-app.${domain}`)
    || (regionId === 'us' && ['app.simplysend.email', 'app.simplysend.dev'].includes(hostname));
}

export function getPortalRegions(): PortalRegion[] {
  const raw = import.meta.env.VITE_REGIONAL_PORTALS_JSON;
  if (!raw) return defaults;
  const configured = JSON.parse(raw) as PortalRegion[];
  if (!Array.isArray(configured) || new Set(configured.map(region => region.id)).size !== configured.length) throw new Error('Invalid regional portal configuration');
  return configured.map(region => {
    const expected = defaults.find(item => item.id === region.id);
    const url = new URL(region.origin);
    const loopbackHosts = ['localhost', '127.0.0.1'];
    const localOrigin = loopbackHosts.includes(window.location.hostname) && loopbackHosts.includes(url.hostname)
      && ['http:', 'https:'].includes(url.protocol);
    if (!expected || region.awsRegion !== expected.awsRegion || url.origin !== region.origin
      || (!localOrigin && (url.protocol !== 'https:' || !isRegionalPortalHostname(region.id, url.hostname)))) throw new Error('Invalid regional portal origin');
    for (const alias of region.originAliases || []) {
      const aliasUrl = new URL(alias);
      if (aliasUrl.origin !== alias || aliasUrl.protocol !== 'https:'
        || !isRegionalPortalHostname(region.id, aliasUrl.hostname)) throw new Error('Invalid regional portal alias');
    }
    return region;
  });
}

export function canAccessPortalRegion(region: PortalRegion, email?: string): boolean {
  return region.enabled && (!region.internalOnly || isInternalUser(email));
}

export function getActivePortalRegion(): PortalRegion {
  const regions = getPortalRegions();
  const origin = window.location.origin;
  if (import.meta.env.VITE_REGIONAL_PORTALS_JSON && !regions.some(item => item.origin === origin || item.originAliases?.includes(origin))
    && !['localhost', '127.0.0.1'].includes(window.location.hostname)) throw new Error('Regional portal configuration does not match this hostname');
  const region = regions.find(item => item.origin === origin || item.originAliases?.includes(origin))
    || regions.find(item => item.id === (import.meta.env.VITE_SITE_ID || 'us'));
  if (!region || !region.enabled) throw new Error('This regional portal is not enabled');
  const knownHost = defaults.find(item => new URL(item.origin).hostname === window.location.hostname);
  if (knownHost && knownHost.id !== region.id) throw new Error('Regional portal configuration does not match this hostname');
  if (region.id !== 'us' && !(region.apiUrl && region.publicApiUrl && region.userPoolId && region.clientId && region.oauthDomain)) {
    throw new Error('Configure the regional API and Cognito settings before enabling this portal');
  }
  if (region.userPoolId && !region.userPoolId.startsWith(`${region.awsRegion}_`)) throw new Error('Cognito user pool does not match the portal region');
  return region;
}

export const activePortalRegion = getActivePortalRegion();
export const activePortalSettings: PortalPublicSettings = activePortalRegion.settings || {};
